import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Search, Truck, X } from 'lucide-react';
import { Input } from './input';
import { Badge } from './badge';

/**
 * SupplierAutocomplete - Cautare furnizor cu sugestii instant
 * Similar cu ArticleAutocomplete dar pentru furnizori
 */
export const SupplierAutocomplete = ({ 
  suppliers = [], 
  onSelect, 
  value = null,
  placeholder = "Cauta furnizor...",
  className = "",
  excludeIds = [],
  triggerClassName = ""
}) => {
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const prevValueRef = useRef(value);
  const isSelectingRef = useRef(false);

  // Set display value when value prop changes (controlled mode)
  useEffect(() => {
    // Only run when value actually changes, not on suppliers reload
    if (prevValueRef.current !== value) {
      prevValueRef.current = value;
      if (value && !isSelectingRef.current) {
        const supplier = suppliers.find(s => s.id === value);
        if (supplier) {
          setInputValue(supplier.name);
        }
      } else if (!value && !isSelectingRef.current) {
        // Clear input when value is cleared externally
        setInputValue('');
      }
    }
    isSelectingRef.current = false;
  }, [value, suppliers]);

  // Memoized filter logic - only compute when inputValue or suppliers change
  const filteredSuppliers = useMemo(() => {
    if (inputValue.trim().length < 1) {
      return [];
    }
    const query = inputValue.toLowerCase();
    return suppliers.filter(s => 
      !excludeIds.includes(s.id) &&
      (s.name?.toLowerCase().includes(query) ||
       s.cif?.toLowerCase().includes(query) ||
       s.xrp_code?.toLowerCase().includes(query))
    ).slice(0, 50);
  }, [inputValue, suppliers, excludeIds]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectSupplier = useCallback((supplier) => {
    isSelectingRef.current = true;
    setInputValue(supplier.name);
    setShowSuggestions(false);
    onSelect?.(supplier);
  }, [onSelect]);

  const handleClear = useCallback(() => {
    isSelectingRef.current = true;
    setInputValue('');
    setShowSuggestions(false);
    onSelect?.(null);
  }, [onSelect]);

  const handleKeyDown = useCallback((e) => {
    if (!showSuggestions || filteredSuppliers.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => prev < filteredSuppliers.length - 1 ? prev + 1 : prev);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && filteredSuppliers[selectedIndex]) {
          handleSelectSupplier(filteredSuppliers[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        break;
      default:
        break;
    }
  }, [showSuggestions, filteredSuppliers, selectedIndex, handleSelectSupplier]);

  const handleFocus = useCallback(() => {
    if (inputValue.length >= 1 && filteredSuppliers.length > 0) {
      setShowSuggestions(true);
    }
  }, [inputValue.length, filteredSuppliers.length]);

  const handleInputChange = useCallback((e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setSelectedIndex(-1);
    
    // Show suggestions when typing
    if (newValue.length >= 1) {
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
    
    // If user types and clears the matched value, deselect
    if (value) {
      const supplier = suppliers.find(s => s.id === value);
      if (supplier && newValue !== supplier.name) {
        isSelectingRef.current = true;
        onSelect?.(null);
      }
    }
  }, [value, suppliers, onSelect]);

  return (
    <div ref={wrapperRef} className={`relative ${className}`} data-testid="supplier-autocomplete">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 z-10" />
        <Input
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={`pl-9 pr-8 ${triggerClassName}`}
          autoComplete="off"
          data-testid="supplier-search-input"
        />
        {inputValue && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-100 rounded"
          >
            <X className="w-3 h-3 text-slate-400" />
          </button>
        )}
      </div>

      {showSuggestions && filteredSuppliers.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white rounded-lg shadow-2xl border border-slate-200 max-h-72 overflow-y-auto">
          <div className="p-1">
            {filteredSuppliers.map((supplier, index) => (
              <div
                key={supplier.id}
                onClick={() => handleSelectSupplier(supplier)}
                onMouseEnter={() => setSelectedIndex(index)}
                className={`p-3 rounded-md cursor-pointer transition-all ${
                  index === selectedIndex 
                    ? 'bg-blue-50 border border-blue-300' 
                    : 'hover:bg-slate-50 border border-transparent'
                }`}
                data-testid={`supplier-option-${supplier.id}`}
              >
                <div className="flex items-center gap-2">
                  <Truck className="w-4 h-4 text-blue-600 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-slate-900 truncate">{supplier.name}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      {supplier.cif && (
                        <span className="text-xs text-slate-500 font-mono">CIF: {supplier.cif}</span>
                      )}
                      {supplier.xrp_code && (
                        <Badge variant="outline" className="text-xs py-0">{supplier.xrp_code}</Badge>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="px-3 py-1.5 bg-slate-50 border-t text-center">
            <p className="text-xs text-slate-500">{filteredSuppliers.length} rezultate</p>
          </div>
        </div>
      )}

      {showSuggestions && filteredSuppliers.length === 0 && inputValue.length >= 1 && (
        <div className="absolute z-50 w-full mt-1 bg-white rounded-lg shadow-xl border border-slate-200 p-3">
          <p className="text-sm text-slate-500 text-center">Niciun furnizor gasit pentru "{inputValue}"</p>
        </div>
      )}
    </div>
  );
};

export default SupplierAutocomplete;
